import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import { MiPlaceholderArea } from "@miplatform/components";
import { FONT_WEIGHTS } from "@miplatform/constants";
import { IPermissionCategory } from "@miplatform/types";
import { Box, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";

export const RolePermissionsTabView = ({
  extraTabProps,
}: {
  extraTabProps?: {
    permissionTabCategories?: Record<string, IPermissionCategory>;
  };
}) => {
  const { permissionTabCategories = {} } = extraTabProps ?? {};
  const { classes } = useStyles();

  const selectedPermissionTabCategories: Record<string, IPermissionCategory> =
    Object.values(permissionTabCategories).reduce((acc, group) => {
      if (group.selectedPermissions.length > 0) {
        acc[group.key] = group;
      }
      return acc;
    }, {} as Record<string, IPermissionCategory>);

  return (
    <ConfigAccordionHeader title="Selected Permissions">
      {!permissionTabCategories ||
      Object.keys(selectedPermissionTabCategories).length === 0 ? (
        <NoPermissionsPlaceholder />
      ) : (
        <Box className={classes.permissionGroupCardsContainer}>
          {Object.entries(selectedPermissionTabCategories).map(
            ([groupName, group]) => (
              <PermissionGroupCard
                key={groupName}
                groupName={group.name}
                selectedCount={group.selectedPermissions.length}
              />
            )
          )}
        </Box>
      )}
    </ConfigAccordionHeader>
  );
};

const PermissionGroupCard = ({
  groupName,
  selectedCount,
}: {
  groupName: string;
  selectedCount: number;
}) => {
  const { classes, theme } = useStyles();
  return (
    <Box className={classes.permissionGroupCard}>
      <Box className={classes.permissionGroupCardCount}>
        <Typography variant="BD1">{selectedCount}</Typography>
      </Box>
      <Typography
        variant="BD2"
        color={theme.palette.primary.main}
        fontWeight={FONT_WEIGHTS.BOLD}
      >
        {groupName}
      </Typography>
    </Box>
  );
};

const NoPermissionsPlaceholder = () => {
  const theme = useTheme();
  return (
    <MiPlaceholderArea
      text="No Selected Permissions"
      helperText="Tip: Select your permissions to see them here."
      iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
      containerStyle={{
        backgroundColor: theme.palette.primary.light,
        flex: 1,
        minHeight: 500,
      }}
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  permissionGroupCardsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey.lightAccent,
    padding: theme.spacing(4),
    borderRadius: 8,
  },
  permissionGroupCard: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    display: "flex",
    gap: theme.spacing(2),
    flexDirection: "row",
    alignItems: "center",
    color: theme.palette.text.primary,
  },
  permissionGroupCardCount: {
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.primary.medium}`,
    color: theme.palette.primary.medium,
    padding: theme.spacing(0, 1),
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));
